import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useMemo } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OrderStatus } from '../../../../../../../enums/orders';
import { Product } from '../../../../../../../enums/product';
import { Service } from '../../../../../../../enums/service';
import { LabelValue } from '../../../../../../../types/options';
import { invalidDecimalPointCharactersRegex } from '../../../../../../../utils/constants/regex';
import { FormValues } from '../../CreateEditDialog.functions';

type Props = {
  idx: number;
  serviceLabels: Record<Service, string>;
  serviceOptions: LabelValue<Service>[];
  productLabels: Record<Product, string>;
  productOptions: LabelValue<Product>[];
  orderStatusOptions: LabelValue<OrderStatus>[];
  productServiceMapping: [Product, Service][];
  disabled: boolean;
  isLoading: boolean;
};

function Row({
  idx,
  serviceLabels,
  serviceOptions: _serviceOptions,
  productLabels,
  productOptions: _productOptions,
  orderStatusOptions,
  productServiceMapping,
  disabled,
  isLoading,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const popust = useWatch<FormValues, 'popust'>({
    name: 'popust',
  });

  const { remove } = useFieldArray<FormValues, 'popust'>({
    name: 'popust',
  });

  const productId = popust ? popust[idx].ProizvodId : null;
  const serviceId = popust ? popust[idx].UslugaId : null;

  const serviceOptions = useMemo<LabelValue<Service>[]>(() => {
    if (!productId) {
      return _serviceOptions;
    }

    const allowedProductServices = productServiceMapping.filter(
      ([product]) => product === productId
    );

    return allowedProductServices.map(([_, service]) => ({
      label: serviceLabels[service],
      value: service,
    }));
  }, [_serviceOptions, productId, productServiceMapping, serviceLabels]);

  const productOptions = useMemo<LabelValue<Product>[]>(() => {
    if (!serviceId) {
      return _productOptions;
    }

    const allowedProductproducts = productServiceMapping.filter(
      ([_, service]) => service === serviceId
    );

    return allowedProductproducts.map(([product]) => ({
      label: productLabels[product],
      value: product,
    }));
  }, [_productOptions, productLabels, productServiceMapping, serviceId]);

  return (
    <>
      <div>{idx + 1}.</div>

      <div>
        {/* <FieldWithErrors name={`popust.${idx}.UslugaId`} label={false}> */}
        <Controller
          name={`popust.${idx}.UslugaId`}
          render={({ field }) => (
            <Dropdown
              placeholder={isLoading ? t('Loading...') : t('All')}
              id={`popust.${idx}.UslugaId`}
              name={`popust.${idx}.UslugaId`}
              options={serviceOptions}
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
            />
          )}
        />
        {/* </FieldWithErrors> */}
      </div>

      <div>
        {/* <FieldWithErrors name={`popust.${idx}.ProizvodId`} label={false}> */}
        <Controller
          name={`popust.${idx}.ProizvodId`}
          render={({ field }) => (
            <Dropdown
              placeholder={isLoading ? t('Loading...') : t('All')}
              id={`popust.${idx}.ProizvodId`}
              name={`popust.${idx}.ProizvodId`}
              options={productOptions}
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
            />
          )}
        />
        {/* </FieldWithErrors> */}
      </div>

      <div>
        {/* <FieldWithErrors name={`popust.${idx}.StatusId`} label={false}> */}
        <Controller
          name={`popust.${idx}.StatusId`}
          render={({ field }) => (
            <Dropdown
              placeholder={isLoading ? t('Loading...') : t('All')}
              id={`popust.${idx}.StatusId`}
              name={`popust.${idx}.StatusId`}
              options={orderStatusOptions}
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
              showClear
            />
          )}
        />
        {/* </FieldWithErrors> */}
      </div>

      <div>
        {/* <FieldWithErrors name={`popust.${idx}.Popust`} label={false}> */}
        <Controller
          name={`popust.${idx}.Popust`}
          render={({ field }) => (
            <InputText
              id={`popust.${idx}.Popust`}
              name={`popust.${idx}.Popust`}
              value={field.value}
              onChange={(e) =>
                field.onChange(
                  e.target.value.replace(invalidDecimalPointCharactersRegex, '')
                )
              }
              disabled={disabled}
            />
          )}
        />
        {/* </FieldWithErrors> */}
      </div>

      <div>
        <Button
          type="button"
          onClick={() => remove(idx)}
          icon="fas fa-times"
          className="p-button-text p-button-plain"
        />
      </div>
    </>
  );
}

export default Row;
